import React from "react";
import { useTranslation } from "react-i18next";

function PrivacyPolicy() {
    const { t } = useTranslation("privacyPolicy");

    return (
        <div className="card">
            <h1>{t("title")}</h1>
            <p>{t("effectiveDate")}</p>

            <h2>1. Introduction</h2>
            <p>
                Welcome to NEURO+, a neuroscience-based mobile application developed and
                operated by BN-Hancement L.L.C. ("we," "us," or "our"). NEURO+ is
                available on iOS and combines brain training mini-games, mental
                regulation tools (guided breathing and meditation), nutrition content
                (recipes and brain boosters), habit tracking, daily missions, streak
                systems, and a behaviour-based Brain Score to support your cognitive and
                overall well-being.
            </p>
            <p>
                This Privacy Policy describes how we collect, use, store, and protect
                your personal information when you use the NEURO+ application ("App").
                By downloading, installing, or using the App, you acknowledge that you
                have read and agree to the practices described in this Privacy Policy.
            </p>
            <p>
                This policy is designed to be compliant with the General Data Protection
                Regulation (GDPR), the California Consumer Privacy Act (CCPA), and the
                Apple App Store Review Guidelines.
            </p>
            <p>
                If you do not agree with the terms of this Privacy Policy, please do not
                use the App.
            </p>

            <h2>2. Data We Collect</h2>
            <p>
                We collect only the information necessary to provide and improve the
                functionality of the App. We do not sell your personal information to
                third parties.
            </p>

            <h2>2.1 Identity and Authentication Data</h2>
            <p>
                Authentication is handled exclusively through Google Sign-In. When you
                create an account or log in, we collect and store the following:
            </p>
            <ul>
                <li>Your Google Sign-In user ID (a unique identifier assigned by Google)</li>
                <li>Your email address associated with your Google account</li>
            </ul>
            <p>
                No passwords are created or stored by NEURO+. We do not have access to
                your Google account credentials.
            </p>

            <h2>2.2 App Usage Data</h2>
            <p>
                As you use the App, we collect data related to your activity in order to
                provide core features and personalise your experience:
            </p>
            <ul>
                <li>Login timestamps and session activity data</li>
                <li>Brain Score values and Brain Score history</li>
                <li>Time spent in brain training games and game result data</li>
                <li>Food logs, including meals and brain-boosting foods recorded by you</li>
                <li>Regulation session completions (breathing exercises and meditation sessions)</li>
                <li>Streak data and daily mission completion records</li>
            </ul>
            <p>
                All data listed above is generated directly by your interactions with
                the App and is not collected passively or inferred from third-party sources.
            </p>

            <h2>2.3 Data We Do NOT Collect</h2>
            <p>We do not collect the following categories of data:</p>
            <ul>
                <li>Precise geolocation or GPS data</li>
                <li>Device contacts, camera, or microphone data</li>
                <li>Biometric data</li>
                <li>Financial or payment information</li>
                <li>Sensitive personal information beyond what is described in this policy</li>
            </ul>

            <h2>3. How We Use Your Data</h2>
            <p>We use the information we collect for the following purposes:</p>
            <ul>
                <li>To authenticate your identity and maintain your account securely via Google Sign-In</li>
                <li>To provide and operate the core features of the App, including brain training games, regulation tools, nutrition content, and habit tracking</li>
                <li>To calculate, display, and maintain your Brain Score and historical performance data</li>
                <li>To track your streaks, daily missions, and progress over time</li>
                <li>To improve, troubleshoot, and personalise the App experience</li>
                <li>To monitor session activity for security and fraud prevention purposes</li>
                <li>To respond to your inquiries or support requests</li>
            </ul>
            <p>We process your data on the following legal bases (as applicable under GDPR):</p>
            <ul>
                <li><strong>Performance of a contract:</strong> Processing necessary to provide the services you have requested through the App</li>
                <li><strong>Legitimate interests:</strong> Processing that is in our legitimate interest, such as ensuring the security and integrity of our services, provided such interests are not overridden by your rights</li>
                <li><strong>Consent:</strong> Where required by applicable law, we will request your consent before processing your data</li>
            </ul>
            <p>
                We do not use your data for advertising purposes, and we do not share
                your data with any third party for marketing or advertising.
            </p>

            <h2>4. Data Storage & Security</h2>
            <p>
                We take the security of your personal data seriously and implement
                appropriate technical and organisational measures to protect it against
                unauthorised access, disclosure, alteration, or destruction.
            </p>

            <h2>4.1 Infrastructure</h2>
            <p>
                All user data is stored in a MySQL relational database hosted on Amazon
                Web Services (AWS) Relational Database Service (RDS). The database is
                not publicly accessible and is protected within a private network environment.
            </p>

            <h2>4.2 Credential Management</h2>
            <p>
                Database credentials are managed through AWS Secrets Manager, which
                provides automatic password rotation. This means that database access
                credentials are regularly rotated without manual intervention, reducing
                the risk of credential compromise.
            </p>

            <h2>4.3 Data Retention</h2>
            <p>
                We retain your personal data for as long as your account is active or
                as necessary to provide you with the App's services. If you request
                deletion of your account and associated data, we will delete or
                anonymise your personal data within a reasonable timeframe, except where
                we are required to retain certain data by applicable law.
            </p>

            <h2>4.4 Data Transfers</h2>
            <p>
                Your data is stored and processed on servers located within the AWS
                infrastructure. If you are located in the European Economic Area (EEA),
                please be aware that your data may be transferred to and processed in
                countries outside the EEA (including the United States). We ensure that
                such transfers are subject to appropriate safeguards in accordance with
                applicable data protection laws.
            </p>
            <p>
                Although we implement strong security measures, no method of transmission
                over the internet or method of electronic storage is 100% secure. We
                encourage you to use the App responsibly and to contact us if you have
                concerns about your data.
            </p>

            <h2>5. Third-Party Services — Google Sign-In</h2>
            <p>
                The App uses Google Sign-In as its sole method of authentication. When
                you use Google Sign-In to access NEURO+, Google may collect certain data
                in accordance with its own privacy practices.
            </p>
            <p>
                We only receive and store your Google user ID and email address from
                this process. We do not receive or store any other data from your Google
                account (such as contacts, calendar, or other profile data).
            </p>
            <p>
                Your use of Google Sign-In is governed by Google's Privacy Policy and
                Terms of Service:
            </p>
            <ul>
                <li>
                    <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer">
                        Google Privacy Policy
                    </a>
                </li>
                <li>
                    <a href="https://policies.google.com/terms" target="_blank" rel="noreferrer">
                        Google Terms of Service
                    </a>
                </li>
            </ul>
            <p>
                Aside from Google Sign-In, we do not use any third-party analytics SDKs,
                advertising networks, or tracking technologies within the App. We do not
                sell, rent, or otherwise share your personal data with any third party
                for commercial purposes.
            </p>

            <h2>6. Your Rights</h2>
            <p>
                Depending on your jurisdiction, you may have certain rights with respect
                to your personal data. We are committed to honouring these rights in
                accordance with applicable law.
            </p>

            <h2>6.1 Rights Under GDPR (EEA/UK Users)</h2>
            <p>
                If you are located in the European Economic Area or the United Kingdom,
                you have the following rights under the GDPR:
            </p>
            <ul>
                <li><strong>Right of Access:</strong> You have the right to request a copy of the personal data we hold about you.</li>
                <li><strong>Right to Rectification:</strong> You have the right to request that we correct any inaccurate or incomplete personal data we hold about you.</li>
                <li><strong>Right to Erasure ("Right to Be Forgotten"):</strong> You have the right to request that we delete your personal data, subject to certain exceptions under applicable law.</li>
                <li><strong>Right to Restriction of Processing:</strong> You have the right to request that we restrict the processing of your personal data in certain circumstances.</li>
                <li><strong>Right to Data Portability:</strong> You have the right to receive your personal data in a structured, commonly used, and machine-readable format.</li>
                <li><strong>Right to Object:</strong> You have the right to object to the processing of your personal data where we rely on legitimate interests as the legal basis.</li>
                <li><strong>Right to Withdraw Consent:</strong> Where we process your data based on your consent, you have the right to withdraw that consent at any time without affecting the lawfulness of processing carried out prior to withdrawal.</li>
            </ul>

            <h2>6.2 Rights Under CCPA (California Residents)</h2>
            <p>
                If you are a California resident, you have the following rights under
                the California Consumer Privacy Act (CCPA):
            </p>
            <ul>
                <li><strong>Right to Know:</strong> You have the right to request information about the categories and specific pieces of personal information we have collected about you, the sources from which we collected it, the purposes for which we use it, and the categories of third parties with whom we share it.</li>
                <li><strong>Right to Delete:</strong> You have the right to request the deletion of personal information we have collected from you, subject to certain exceptions.</li>
                <li><strong>Right to Non-Discrimination:</strong> We will not discriminate against you for exercising any of your CCPA rights. We do not offer financial incentives in exchange for your personal information.</li>
                <li><strong>Right to Opt-Out of Sale:</strong> We do not sell your personal information. Therefore, no opt-out mechanism for the sale of personal data is required.</li>
            </ul>

            <h2>6.3 How to Exercise Your Rights</h2>
            <p>
                To exercise any of the rights described above, please contact us at:{" "}
                <a href="mailto:appneuroplus@bnhancement.com">
                    appneuroplus@bnhancement.com
                </a>
            </p>
            <p>
                We will respond to your request within the timeframe required by
                applicable law (generally 30 days for GDPR requests and 45 days for
                CCPA requests). We may need to verify your identity before processing
                your request.
            </p>

            <h2>6.4 Account Deletion</h2>
            <p>
                You may request the deletion of your NEURO+ account and all associated
                personal data at any time by contacting us at{" "}
                <a href="mailto:appneuroplus@bnhancement.com">
                    appneuroplus@bnhancement.com
                </a>
                . Upon receiving and verifying your request, we will delete or anonymise
                your data in a timely manner, unless we are required by law to retain
                certain information.
            </p>

            <h2>7. Children's Privacy</h2>
            <p>
                NEURO+ is not directed at or intended for use by children under the age
                of 13 (or under the age of 16 for users located in the European Economic
                Area, in accordance with applicable national laws implementing the GDPR).
            </p>
            <p>
                We do not knowingly collect personal information from children under the
                applicable age threshold. If you are a parent or guardian and believe
                that your child has provided us with personal information without your
                consent, please contact us immediately at{" "}
                <a href="mailto:appneuroplus@bnhancement.com">
                    appneuroplus@bnhancement.com
                </a>
                . Upon receiving such a request, we will take prompt steps to delete the
                information from our records.
            </p>

            <h2>8. Changes to This Privacy Policy</h2>
            <p>
                We may update this Privacy Policy from time to time to reflect changes
                in our practices, technology, legal requirements, or other factors. When
                we make changes, we will update the "Effective Date" at the top of this
                document.
            </p>
            <p>
                We encourage you to review this Privacy Policy periodically to stay
                informed about how we are protecting your personal information. Your
                continued use of the App after any changes to this Privacy Policy
                constitutes your acceptance of the updated policy.
            </p>

            <h2>9. Contact Information</h2>
            <p>
                If you have any questions, concerns, or requests regarding this Privacy
                Policy or our data practices, please contact us at:
            </p>
            <p>
                <strong>BN-Hancement L.L.C.</strong>
                <br />
                Email:{" "}
                <a href="mailto:appneuroplus@bnhancement.com">
                    appneuroplus@bnhancement.com
                </a>
            </p>
            <p>
                We are committed to working with you to resolve any concerns about your
                privacy promptly and fairly.
            </p>
        </div>
    );
}

export default PrivacyPolicy;